
<template>
  <div class="add-scheduled-task-form">
    <loading-placeholder v-if="isLoading" :lines="20" />
    <template v-else>
      <div class="add-scheduled-task-form__main">
        <div class="marker--add-task-form--meta-section meta-section">
          <!-- Name -->
          <masthead-input
            v-model="name"
            :is-focused="focusName"
            :class="'in-drawer'"
            class="marker--add-task-form--title title"
            placeholder="Enter event name..."
            maxlength="255"
          />
          <div>
            <div class="label"><i class="ddi ddi-calendar"/>Create Task Date</div>
            <div>
              <due-date-picker
                v-model="eventDate"
              />
            </div>
          </div>
          <div v-if="changesFollowingEvents" class="date-warning marker--add-scheduled-task-form--following-events-warning">
            <i class="ddi ddi-warning" />
            <span>This changes this and all following events. Earlier events stay as they are.</span>
          </div>
          <div v-if="isPastEventDate" class="date-warning marker--add-scheduled-task-form--past-date-warning">
            <i class="ddi ddi-warning" />
            <span>{{ pastEventDateWarning }}</span>
          </div>
          <div>
            <div class="label"><i class="ddi ddi-refresh"/>Repeat</div>
            <select-button
              v-model="recurrenceFrequencyType"
              :options="recurrenceFrequencyTypes"
              :size="'small'"
              title="Recurrence"
            />
          </div>
          <div v-if="recurrenceFrequencyType !== 1 && recurrenceFrequencyType === 5">
            <div class="label"><i class="ddi ddi-refresh"/>Monthly On</div>
            <select-button
              v-model="recurrenceFrequencyOccurrence"
              :options="recurrenceFrequencyOccurrences"
              :size="'small'"
              title="Monthly On"
            />
          </div>
          <div v-if="recurrenceFrequencyType !== 1">
            <div class="label"><i class="ddi ddi-return" />Every</div>
            <el-input
              v-model.number="recurrenceInterval"
              :size="'small'"
              title="Interval"
              type="number"
              style="width: 80px;"
              min="0"
              max="99"/>
            <span>
              {{ recurrenceFrequencyTypes.find(type => type.id === recurrenceFrequencyType).shortName }}{{ recurrenceInterval > 1 ? 's' : '' }}
            </span>
          </div>
          <div v-if="recurrenceFrequencyType !== 1">
            <div class="label"><i class="ddi ddi-circle-error" />Ends</div>
            <select-button
              v-model="recurrenceEndType"
              :options="recurrenceEndTypes"
              :size="'small'"
              title="End Type"
            />
          </div>
          <div v-if="recurrenceFrequencyType !== 1 && recurrenceEndType === 2">
            <div class="label"><i class="ddi ddi-calendar" />End Date</div>
            <due-date-picker
              v-model="recurrenceEndDate"
            />
          </div>
          <div v-if="recurrenceFrequencyType !== 1 && recurrenceEndType === 3">
            <div class="label"><i class="ddi">#</i> After</div>
            <el-input
              v-model.number="recurrenceEndAfterOccurrences"
              :size="'small'"
              title="End Count"
              type="number"
              style="width: 80px;"
              min="0"
              max="99"/>
            <span>
              Occurrence{{ recurrenceEndAfterOccurrences > 1 ? 's' : '' }}
            </span>
          </div>
        </div>
        
        <div class="marker--add-task-form--meta-section meta-section">
          <!-- Title -->
          <masthead-input
            v-model="taskTitle"
            :is-focused="focusTitle"
            :class="'in-drawer'"
            :variables="variables"
            class="marker--add-task-form--title title"
            placeholder="Enter task title..."
            maxlength="255"
          />
          <!-- Priority -->
          <div>
            <div class="label"><i class="ddi ddi-warning" />Priority</div>
            <priority-picker
              v-model:selected-id="taskPriorityLevelID"
              :options="priorityOptions"
              :size="pickerSize"
            />
          </div>
          <!-- Assignee -->
          <div>
            <div class="label"><i class="ddi ddi-unassigned"/>Assignee</div>
            <assignment-picker
              v-model:selected-id="taskAssigneeID"
              :options="assigneesOptions"
              :size="pickerSize"
            />
          </div>
          <!-- Status -->
          <div>
            <div class="label"><i class="ddi ddi-workflows" />Status</div>
            <status-picker
              v-model:selected-id="taskStatusID"
              :statuses="statusOptions"
              :size="pickerSize"
            />
          </div>
          <!-- Tags -->
          <div class="small-pad">
            <div class="label"><i class="ddi ddi-tag" />Tags</div>
            <tags-input
              v-model:selected-tags="taskTags"
              :available-tags="tagsOptions"
              hide-icon
              class="tags-input"
            />
          </div>
          <!-- Watching -->
          <div>
            <div class="label"><i class="ddi ddi-watching" />Watchers</div>
            <watchers-picker
              v-model:selected-ids="taskWatcherIDs"
              :options="watchersOptions"
              :current-user-id="$permissions.loggedInUser.id"
              :type="EEntities.PROJECTS"
              hide-instructions
              from-drawer
              class="watchers-picker"
            />
          </div>
          
          <!-- Due Date -->
          <div>
            <div class="label"><i class="ddi ddi-calendar"/>Due After</div>
            <el-input
              v-if="dueDateOffsetType != 0"
              v-model.number="dueDateOffset"
              :size="'small'"
              title="Due Date Offset"
              type="number"
              style="width: 80px;"
              min="0"
              max="99"/>
            <select-button
              v-model="dueDateOffsetType"
              :options="dueDateOffsetTypes"
              :size="'small'"
              title="Due Date Offset Type"
            />
          </div>
        </div>
        <div>
          <br>
        </div>
        <!-- Description -->
        <editor
          v-model="taskDescription"
          :mention-users="mentionOptions"
          :saved-templates="descriptionTemplates"
          :custom-sticky-top-pixels="customStickyTopPixels"
          :variables="variables"
          class="marker--add-task-form--description"
          placeholder="Write a description..."
        />
        
      </div>
    </template>
    <div class="add-task-form__footer">
      <dd-button
        :is-disabled="isSubmitting"
        :is-loading="isSubmitting"
        :color="ColorAssistant.ProjectColor"
        size="large"
        @click="validateAndSubmit()"
      >{{existingScheduledTaskId == null ? 'Create event' : 'Update event'}}</dd-button>
      <dd-button
        :is-disabled="isSubmitting"
        type="text"
        color="grey"
        size="large"
        @click="handleCancelClick()"
      >Cancel</dd-button>
      <dd-button 
        v-if="existingScheduledTaskId != null"
        :is-disabled="isSubmitting"
        :is-loading="isSubmitting"
        type="text"
        class="scary-list-option"
        size="large"
        @click="handleDeleteClick()"
      >
        <i class="ddi ddi-trash" /> Delete?
      </dd-button>
    </div>

    <!-- Which occurrences of a repeating event to delete -->
    <dd-modal
      v-model:visible="showDeleteScopeModal"
      desktop-sizing="small"
      append-to-body
      class="scheduled-task-delete-scope-modal"
      title="Delete repeating event"
    >
      <el-radio-group v-model="deleteScope" class="delete-scopes">
        <el-radio value="this">This event</el-radio>
        <el-radio value="following">This and following events</el-radio>
        <el-radio value="all">All events</el-radio>
      </el-radio-group>
      <template #footer>
        <span>
          <dd-button type="text" @click="showDeleteScopeModal = false">Cancel</dd-button>
          <dd-button
            :is-loading="isDeleting"
            :is-disabled="isDeleting"
            color="red"
            class="marker--scheduled-task-delete-scope-modal--delete"
            @click="deleteScheduledTask(deleteScope)"
          >Delete</dd-button>
        </span>
      </template>
    </dd-modal>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import dayjs from '@/helpers/dayjs'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data objects */
import { EEntities } from '@/types'
import {
  IAtMentionUser,
  IDescriptionTemplate,
  IJob,
  IJobAssigneeMandate,
  INameAndDescription,
  IPersonWithEmail,
  IPriorityLevelForSelection,
  IRecurrence,
  IStatusForSelection,
} from '@/api/types/response.types'

/* API */
import TasksAPI from '@/api/modules/tasks.api'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'

export default defineComponent({
  name: 'AddScheduledTaskForm',

  props: {
    selectedProjectId: {
      type: Number,
      required: true,
    },
    existingScheduledTaskId: {
      type: Number,
      default: null,
    },
    customStickyTopPixels: {
      type: Number,
      default: null,
    },
    selectedDate: {
      type: Date,
      required: true,
    },
  },

  emits: ['canceled', 'scheduled-task-created', 'scheduled-task-deleted'],

  data() {
    return {
      EEntities,
      ColorAssistant,
      browser: this.$deviceInfo.getBrowser,

      // UI controls
      isSubmitting: false,
      isDeleting: false,
      showDeleteScopeModal: false,
      deleteScope: 'this',

      // Focus controls
      focusName: false,
      focusTitle: false,

      isLoading: false,

      // Options
      statusOptions: [] as IStatusForSelection[],
      priorityOptions: [] as IPriorityLevelForSelection[],
      assigneesOptions: [] as IPersonWithEmail[],
      tagsOptions: [] as string[],
      watchersOptions: [] as IPersonWithEmail[],
      mentionOptions: [] as IAtMentionUser[],
      descriptionTemplates: [] as IDescriptionTemplate[],

      jobs: [] as IJob[],
      assigneeMandates: [] as IJobAssigneeMandate[],

      // Form
      name: '',
      // Local date; toISOString() would give the day before east of UTC
      eventDate: dayjs(this.selectedDate).format('YYYY-MM-DD'),
      // For existing events: the occurrence that was opened, and how the event repeated before any changes
      occurrenceDate: dayjs(this.selectedDate).format('YYYY-MM-DD'),
      originalRecurrence: null as IRecurrence | null,
      taskTitle: '',
      taskDescription: '',
      taskStatusID: null as number | null,
      taskPriorityLevelID: null as number | null,
      taskAssigneeID: null as number | null,
      taskTags: [] as string[],
      taskWatcherIDs: [] as number[],
      recurrenceFrequencyType: 1, // no recurrence by default
      recurrenceFrequencyTypes: [
        { id: 1, name: 'Do Not Repeat', shortName: '' },
        { id: 3, name: 'Daily', shortName: 'Day' },
        { id: 4, name: 'Weekly', shortName: 'Week' },
        { id: 5, name: 'Monthly', shortName: 'Month' },
        { id: 6, name: 'Yearly', shortName: 'Year' },
      ] as { id: number; name: string; shortName?: string }[],
      recurrenceFrequencyOccurrences: [
        { id: 0, name: '' },
        { id: 1, name: 'First' },
        { id: 2, name: 'Second' },
        { id: 3, name: 'Third' },
        { id: 4, name: 'Fourth' },
        { id: 5, name: 'Fifth' },
        { id: -1, name: 'Last' },
        { id: -2, name: 'Second to Last' },
        { id: -3, name: 'Third to Last' },
        { id: -4, name: 'Fourth to Last' },
        { id: -5, name: 'Fifth to Last' },
      ] as { id: number; name: string }[],
      recurrenceFrequencyOccurrence: 0,
      recurrenceInterval: 1, // default interval for recurrence
      recurrenceEndType: 1, // default no end
      recurrenceEndTypes: [
        { id: 1, name: 'Never' },
        { id: 2, name: 'On Date' },
        { id: 3, name: 'After Occurrences' },
      ] as { id: number; name: string }[],
      recurrenceEndDate: null as string | null,
      recurrenceEndAfterOccurrences: 1,
      variables: [
        { name: '{EventDate}', description: 'Event - Date' },
        { name: '{EventYear}', description: 'Event - Year' },
        { name: '{EventQuarter}', description: 'Event - Quarter' },
        { name: '{EventMonth}', description: 'Event - Month' },
        { name: '{EventMonthName}', description: 'Event - Month Name' },
        { name: '{EventWeek}', description: 'Event - Week' },
        { name: '{EventDay}', description: 'Event - Day' },
        { name: '{DueDateDate}', description: 'Due Date - Date' },
        { name: '{DueDateYear}', description: 'Due Date - Year' },
        { name: '{DueDateQuarter}', description: 'Due Date - Quarter' },
        { name: '{DueDateMonth}', description: 'Due Date - Month' },
        { name: '{DueDateMonthName}', description: 'Due Date - Month Name' },
        { name: '{DueDateWeek}', description: 'Due Date - Week' },
        { name: '{DueDateDay}', description: 'Due Date - Day' },
      ] as INameAndDescription[],
      dueDateOffsetType: 0,
      dueDateOffsetTypes: [
        { id: 0, name: 'No Due Date' },
        { id: 1, name: 'Days' },
        { id: 2, name: 'Weeks' },
        { id: 3, name: 'Months' },
        { id: 4, name: 'Years' },
      ] as { id: number; name: string; shortName?: string }[],
      dueDateOffset: 1,
    }
  },

  computed: {
    pickerSize(): string {
      return this.$screen.isTabletOrMobile(this.$mq) ? 'medium' : 'small'
    },
    projectID(): number {
      return this.selectedProjectId
    },
    isEventDateChanged(): boolean {
      return (
        this.existingScheduledTaskId != null &&
        !!this.eventDate &&
        this.eventDate.slice(0, 10) !== this.occurrenceDate
      )
    },
    // New or moved events can start in the past, but tasks are only created for dates from today on
    isPastEventDate(): boolean {
      return (
        (this.existingScheduledTaskId == null || this.isEventDateChanged) &&
        !!this.eventDate &&
        this.eventDate.slice(0, 10) < dayjs().format('YYYY-MM-DD')
      )
    },
    // A new date or repeat pattern for a repeating event applies from the opened occurrence on (see UpdateScheduledTask)
    changesFollowingEvents(): boolean {
      const original = this.originalRecurrence
      if (original == null) {
        return false
      }
      return (
        this.isEventDateChanged ||
        this.recurrenceFrequencyType === 1 ||
        this.recurrenceFrequencyType !== original.frequencyType ||
        this.recurrenceInterval !== original.interval ||
        (this.recurrenceFrequencyType === 5 &&
          this.recurrenceFrequencyOccurrence !== original.monthlyByOccurrences)
      )
    },
    pastEventDateWarning(): string {
      return this.recurrenceFrequencyType === 1
        ? 'This date is in the past, so no task will be created for this event.'
        : 'This date is in the past. Tasks will only be created for occurrences from today on.'
    },
  },

  watch: {
    eventDate(newVal: string) {
      if (newVal) {
        this.updateRecurrenceOccurrenceNames(
          new Date(newVal + (newVal.length <= 10 ? 'T00:00:00' : '')),
        )
      }
    },
  },

  async created() {
    this.isLoading = true
    try {
      await Promise.all([this.fetchOptions()])
      this.defaultRequiredFields()
    } catch (error) {
      this.$error.emit(error)
    }

    if (this.existingScheduledTaskId != null) {
      try {
        const scheduledTask = await InternalProjectsAPI.get_ScheduledTasksGet(
          this.projectID,
          this.existingScheduledTaskId,
        )
        // The date shown stays on the opened occurrence. A one-time event has only its own date, so a stale date from
        // a link can't reschedule it on save.
        this.originalRecurrence = scheduledTask.recurrence
        if (!scheduledTask.recurrence) {
          this.eventDate = scheduledTask.eventDate.slice(0, 10)
          this.occurrenceDate = this.eventDate
        }
        this.name = scheduledTask.name
        this.taskTitle = scheduledTask.taskTitle
        this.taskDescription = scheduledTask.taskDescription
        this.taskStatusID = scheduledTask.taskStatusID
        this.taskTags = scheduledTask.taskTags
        this.taskWatcherIDs = scheduledTask.taskWatcherIDs || []
        this.taskPriorityLevelID = scheduledTask.taskPriorityLevelID
        this.taskAssigneeID = scheduledTask.taskAssigneeID
        this.dueDateOffsetType = scheduledTask.dueDateOffsetType
        this.dueDateOffset = scheduledTask.dueDateOffset || 1

        if (scheduledTask.recurrence) {
          this.recurrenceFrequencyType = scheduledTask.recurrence.frequencyType
          this.recurrenceInterval = scheduledTask.recurrence.interval
          this.recurrenceFrequencyOccurrence =
            scheduledTask.recurrence.monthlyByOccurrences
          if (scheduledTask.recurrence.count) {
            this.recurrenceEndType = 3
            this.recurrenceEndAfterOccurrences = scheduledTask.recurrence.count
          } else if (scheduledTask.recurrence.until) {
            this.recurrenceEndType = 2
            this.recurrenceEndDate = scheduledTask.recurrence.until
          } else {
            this.recurrenceEndType = 1
          }
        }
      } catch (error) {
        this.$error.emit(error)
      }
    }

    this.updateRecurrenceOccurrenceNames(new Date(this.selectedDate))

    this.isLoading = false
  },

  methods: {
    updateRecurrenceOccurrenceNames(date: Date) {
      const baseNames: Record<number, string> = {
        0: '',
        1: 'First',
        2: 'Second',
        3: 'Third',
        4: 'Fourth',
        5: 'Fifth',
        [-1]: 'Last',
        [-2]: 'Second to Last',
        [-3]: 'Third to Last',
        [-4]: 'Fourth to Last',
        [-5]: 'Fifth to Last',
      }
      const dayOfMonth = date.getDate()
      const dayOfWeek = date.toLocaleDateString('default', { weekday: 'long' })
      this.recurrenceFrequencyOccurrences.forEach(item => {
        if (item.id === 0) {
          item.name = `Day ${dayOfMonth}`
        } else {
          item.name = `${baseNames[item.id]} ${dayOfWeek}`
        }
      })
    },

    defaultRequiredFields() {
      this.taskStatusID = this.statusOptions[0].id
      this.taskPriorityLevelID = this.priorityOptions[0].id
    },

    async fetchOptions() {
      const response = await TasksAPI.get_FilterOptions(this.projectID)

      this.statusOptions = response.listStatuses
      this.priorityOptions = response.listPriorities
      this.watchersOptions = response.listWatchers
      this.assigneesOptions = response.listAssignees
      this.tagsOptions = response.listTags
      this.mentionOptions = response.listAtMentionUsers
      this.descriptionTemplates = response.listDescriptionTemplates
    },

    handleCancelClick() {
      this.$emit('canceled')
    },

    handleDeleteClick() {
      // Repeating events ask which occurrences to delete, like other calendars do
      if (this.originalRecurrence != null) {
        this.deleteScope = 'this'
        this.showDeleteScopeModal = true
        return
      }

      this.$confirm(`${this.name}`, {
        center: true,
        confirmButtonText: 'Yes, Delete',
        confirmButtonClass: 'el-button--strawberry',
        cancelButtonText: 'Cancel',
        cancelButtonClass: 'el-button--text',
        type: 'warning',
        title: 'Are you sure you want to delete this event?',
      })
        .then(() => this.deleteScheduledTask('all'))
        .catch(() => {
          // Canceled
        })
    },

    // scope is "this", "following" or "all", counted from the opened occurrence
    async deleteScheduledTask(scope: string) {
      this.isDeleting = true
      try {
        await InternalProjectsAPI.delete_ScheduledTasksDelete(
          this.projectID,
          this.existingScheduledTaskId,
          this.occurrenceDate,
          scope,
        )
        this.showDeleteScopeModal = false
        this.$emit('scheduled-task-deleted', this.existingScheduledTaskId)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isDeleting = false
      }
    },

    async validateAndSubmit() {
      if (this.eventDate == null) {
        this.$messaging.showError('Please select a date to create the task.')
        return
      }
      if (this.name.trim().length === 0) {
        this.$messaging.showError('Please enter an event name.')
        this.focusTitle = false
        this.focusName = true
        return
      }
      if (this.taskTitle.trim().length === 0) {
        this.$messaging.showError('Please enter a title.')
        this.focusName = false
        this.focusTitle = true
        return
      }

      try {
        const formData = {
          id: 0,
          projectID: this.projectID,
          name: this.name,
          eventDate: this.eventDate,
          taskTitle: this.taskTitle,
          taskDescription: this.taskDescription,
          taskStatusID: this.taskStatusID,
          taskTags: this.taskTags,
          taskWatcherIDs: this.taskWatcherIDs,
          taskPriorityLevelID: this.taskPriorityLevelID,
          taskAssigneeID:
            this.taskAssigneeID === 0 ? null : this.taskAssigneeID,
          dueDateOffsetType: this.dueDateOffsetType,
          dueDateOffset:
            this.dueDateOffsetType === 0 ? null : this.dueDateOffset,
          recurrence:
            this.recurrenceFrequencyType === 1
              ? null
              : {
                  frequencyType: this.recurrenceFrequencyType,
                  interval: this.recurrenceInterval,
                  monthlyByOccurrences: this.recurrenceFrequencyOccurrence,
                  count:
                    this.recurrenceEndType === 3
                      ? this.recurrenceEndAfterOccurrences
                      : null,
                  until:
                    this.recurrenceEndType === 2
                      ? this.recurrenceEndDate
                      : null,
                },
        }
        if (this.existingScheduledTaskId != null) {
          const task = await InternalProjectsAPI.put_ScheduledTasksUpdate(
            this.projectID,
            this.existingScheduledTaskId,
            {
              scheduledTask: formData,
              occurrenceDate: this.occurrenceDate,
            },
          )
          this.$emit('scheduled-task-created', task)
        } else {
          const task = await InternalProjectsAPI.post_ScheduledTasksCreate(
            this.projectID,
            {
              scheduledTask: formData,
            },
          )
          this.$emit('scheduled-task-created', task)
        }
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
  },
})
</script>

<style lang="scss">
.add-scheduled-task-form {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__main {
    /* Room for sticky footer */
    @include media-breakpoint-up(md) {
      padding-bottom: 120px;
    }

    .title {
      margin: 10px 0 30px 0;

      &.in-drawer {
        margin: 0 0 20px 0;
        .el-textarea__inner {
          @include lettering--h2;
          font-size: 20px !important;
          font-weight: 400 !important;
        }
      }
    }

    .el-button {
      padding: 10px 10px !important;
    }

    @include media-breakpoint-down(sm) {
      .insert-variable-dropdown {
        span {
          font-size: 0;
          i {
            font-size: initial !important;
          }
        }
      }
    }

    .meta-section {
      margin-top: 20px;
      padding: 2px 14px;
      border-radius: 8px;
      border: $border--dotted-medium;

      @include media-breakpoint-down(sm) {
        border: none;
        padding: 0;
      }

      > div {
        display: flex;
        align-items: center;
        padding: 12px 0;
        border-bottom: $border--dotted-light;
        @include flex-row-horizontal-spacing(8px);

        .label {
          @include lettering--p-sm;
          font-weight: $bold;
          min-width: 150px;
          max-width: 150px;
          @include single-line-ellipsis;

          > .ddi {
            color: var(--color-text-third);
            padding-right: 4px;
          }

          @include media-breakpoint-down(sm) {
            min-width: 100px;
            max-width: 100px;
          }
        }

        &.small-pad {
          padding: 5px 0;
        }

        &.date-warning {
          @include lettering--p-sm;

          > .ddi {
            color: $color--orange;
          }
        }

        .tags-input {
          left: -8px;
          position: relative;
          width: 100%;
        }

        &:last-child {
          border-bottom: none;
        }
      }
    }
  }

  &__footer {
    display: flex;
    padding: 20px 0;
    flex-direction: row;
    justify-content: flex-start;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 20px 30px;
    background-color: var(--color-general-white);
    border-top: solid 1px $silver-two;
    /* Above editor */
    z-index: 2;

    button {
      &:first-child {
        margin-right: 14px;
      }
      &:nth-child(2) {
        margin-right: 14px;
      }
    }

    @include media-breakpoint-down(sm) {
      position: static;
      border: none;
      flex-direction: column;
      padding: 0;
      margin-top: 40px;

      button {
        width: 100%;
        margin-bottom: 14px;
      }
    }
  }
}

// Appended to the body, so outside .add-scheduled-task-form
.scheduled-task-delete-scope-modal {
  .delete-scopes {
    display: flex;
    flex-direction: column;

    .el-radio {
      margin: 0 0 14px 0;
    }
  }
}
</style>
